import { ButtonWidth, formatPrice } from '@leland-dev/leland-ui-library';
import { type FC, useEffect, useMemo } from 'react';

import { type Scalars } from '../../../../__generated-gql-types__/globalTypes';
import LoadingIndicator from '../../../common/LoadingIndicator';
import CheckboxInput from '../../../inputs/CheckboxInput';

import {
  BaseScheduleSubmissionModalLayout,
  type BaseScheduleSubmissionModalLayoutProps,
} from './BaseScheduleSubmissionModalLayout';
import { PackageSelectionCard } from './PackageSelectionCard';
import {
  type PackageSelectionCoachFragment,
  type PackageSelectionOfferingPackageFragment,
  usePackageSelectionLelandPackagesQuery,
} from './__generated-gql-types__/PackageSelection.generated';

interface PackageSelectionProps extends BaseScheduleSubmissionModalLayoutProps {
  categoryUrn: Nullable<Scalars['CategoryUrn']['output']>;
  coach: BaseScheduleSubmissionModalLayoutProps['coach'] &
    PackageSelectionCoachFragment;
  offeringPackages: Map<string, PackageSelectionOfferingPackageFragment>;
  setOfferingPackages: (
    packages: Map<string, PackageSelectionOfferingPackageFragment>,
  ) => void;
  isPayAsYouGo: boolean;
  setIsPayAsYouGo: (isPayAsYouGo: boolean) => void;
  onMoveToSubmissionStep: () => void;
}

export const PackageSelection: FC<PackageSelectionProps> = ({
  categoryUrn,
  coach,
  sessionType,
  selectedTime,
  setSelectedTime,
  offeringPackages,
  setOfferingPackages,
  isPayAsYouGo,
  setIsPayAsYouGo,
  onMoveToSubmissionStep,
  ...baseScheduleModalProps
}) => {
  const { data: packageData, loading: packageDataLoading } =
    usePackageSelectionLelandPackagesQuery({
      variables: {
        categoryUrn: categoryUrn ?? '',
        tier: coach.tier,
        coachUrn: coach.id,
      },
      skip: !categoryUrn,
    });

  const onlyUpdatedPackages = useMemo(
    () =>
      packageData?.lelandPackages.results.filter(
        (pkg) =>
          pkg.prices?.length &&
          pkg.prices.every((price) => price.tier && price.sku),
      ),
    [packageData?.lelandPackages.results],
  );

  useEffect(() => {
    if (!packageDataLoading && !onlyUpdatedPackages?.length) {
      setIsPayAsYouGo(true);
      onMoveToSubmissionStep();
    }
  }, [
    onlyUpdatedPackages,
    setIsPayAsYouGo,
    onMoveToSubmissionStep,
    packageDataLoading,
  ]);

  return (
    <BaseScheduleSubmissionModalLayout
      buttonProps={{
        onClick: onMoveToSubmissionStep,
        disabled: !offeringPackages.size && !isPayAsYouGo,
        width: ButtonWidth.FULL_MOBILE,
      }}
      coach={coach}
      selectedTime={selectedTime}
      setSelectedTime={setSelectedTime}
      sessionType={sessionType}
      preventScroll
      sidebarOptions={{
        title: 'Select the offerings you’re interested in.',
        subtitle:
          'Select all the options you might be interested in. Your coach will talk you through each of these options during your intro call.',
      }}
      {...baseScheduleModalProps}
    >
      <div className="flex w-full flex-col overflow-scroll bg-leland-gray-hover pb-6 sm:px-10 sm:py-6">
        <div className="bg-leland-white p-6 sm:bg-transparent sm:p-0">
          <h2 className="text-lg font-medium">
            Which plans are you interested in?
          </h2>
          <p className="mt-1 text-leland-gray-light">
            This will help your coach understand your goals and sets your intro
            call up for success.
          </p>
        </div>
        {packageDataLoading ? (
          <div className="flex w-full grow items-center justify-center">
            <div className="size-20 ">
              <LoadingIndicator />
            </div>
          </div>
        ) : (
          <div className="flex w-full flex-col gap-4 px-4 py-6 sm:mt-4 sm:grid sm:grid-cols-2 sm:p-0">
            {onlyUpdatedPackages?.map((pkg) => (
              <PackageSelectionCard
                key={pkg.id}
                coach={coach}
                offeringPackage={pkg}
                isSelected={!!offeringPackages.get(pkg.id)}
                onSelect={(selected) => {
                  if (selected) {
                    setOfferingPackages(
                      new Map(offeringPackages.set(pkg.id, pkg)),
                    );
                  } else {
                    offeringPackages.delete(pkg.id);
                    setOfferingPackages(new Map(offeringPackages));
                  }
                }}
              />
            ))}
          </div>
        )}
        <div className="flex items-center p-4 sm:px-0">
          <hr className="grow border-dashed border-leland-gray-extra-light" />
          <span className="-mt-1 inline grow-0 bg-leland-gray-hover px-2 text-leland-gray-light">
            or
          </span>
          <hr className="grow border-dashed border-leland-gray-extra-light" />
        </div>
        <div className="w-full px-4 sm:px-0">
          <button
            type="button"
            className={`flex rounded-lg bg-leland-white ${isPayAsYouGo ? 'border-2 border-leland-gray-dark p-5' : 'border p-[21px]'} w-full`}
            onClick={() => setIsPayAsYouGo(!isPayAsYouGo)}
          >
            <CheckboxInput
              checkboxClassName="mr-4"
              label={
                <div className="flex flex-col items-start">
                  <h3 className="text-lg font-medium">Pay as you go</h3>
                  <p className="text-leland-gray-light">
                    {coach.hourlyRate
                      ? `Hourly coaching starting at ${formatPrice(coach.hourlyRate)}/hr`
                      : 'Purchase sessions individually'}
                  </p>
                </div>
              }
              containerClassName="w-full"
              className="w-full flex-row-reverse justify-between"
              isChecked={isPayAsYouGo}
              onToggle={setIsPayAsYouGo}
            />
          </button>
        </div>
      </div>
    </BaseScheduleSubmissionModalLayout>
  );
};
